import { workPackageQuery, workTaskQuery } from "@common/api"
import { LoadingState } from "@common/components/shared/loading-state"
import NotFound from "@common/components/shared/not-found"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { AxiosError } from "axios"
import { Suspense } from "react"
import type { LoaderCrumb } from "@/components/layout/layout-breadcrumbs"
import { WorkTaskSummary } from "./-components/work-task-summary"

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute("/_layout/work-task/$id")({
  notFoundComponent: () => (
    <NotFound
      contained
      title="Work task not found"
      description="This work task doesn’t exist or may have been removed. Return to the previous page to continue where you left off."
    />
  ),
  loader: async ({ context: { queryClient }, params: { id } }) => {
    const workTaskId = id
    if (!UUID.test(workTaskId)) {
      throw notFound({ routeId: "/_layout/work-task/$id" })
    }

    const workTask = await queryClient
      .ensureQueryData(workTaskQuery({ id: workTaskId }))
      .catch((error: unknown) => {
        if (error instanceof AxiosError && error.response?.status === 404) {
          throw notFound({ routeId: "/_layout/work-task/$id" })
        }
        throw error
      })
    const taskLabel = workTask.number ?? workTask.description ?? "Work Task"
    if (workTask.work_package_id == null) {
      return { breadcrumb: taskLabel }
    }
    const workPackage = await queryClient.ensureQueryData(
      workPackageQuery({ id: workTask.work_package_id }),
    )
    const breadcrumb: LoaderCrumb[] = [
      {
        label: workPackage.number ?? `Work Package ${workPackage.id}`,
        href: `/work-orders/${workPackage.id}`,
      },
      { label: taskLabel },
    ]
    return { breadcrumb }
  },
  component: WorkTaskPage,
})

function WorkTaskPage(): React.ReactElement {
  const { id } = Route.useParams()

  return (
    <Suspense fallback={<LoadingState />}>
      <WorkTaskSummary workTaskId={id} />
    </Suspense>
  )
}
